import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { money, productImages, sized, useApi } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductGrid } from "@/components/ProductCard";
import { EASE, Reveal } from "@/components/Reveal";
import { lenisRef } from "@/lib/lenis";
import { SizeGuide } from "@/components/SizeGuide";
import { SwipeHint } from "@/components/SwipeHint";

const EXTRAS = {
  "sviin-souvenir-shirt": {
    hide: ["835c68be275b4e3e9a84d1289f26ad4e"],
    model: "Model is 5'11\", 200 lbs and wears a Large.",
    sizeChart: {
      sizes: ["S", "M", "L", "XL", "2XL"],
      rows: [
        { name: "Length", cm: [70, 72, 74, 76, 78] },
        { name: "Shoulder", cm: [58, 60, 62, 64, 66] },
        { name: "Chest", cm: [59, 61, 63, 65, 67] },
        { name: "Sleeve", cm: [21, 22, 23, 24, 25] },
      ],
    },
    images: [
      { url: "/img/souvenir-model.webp", altText: "Model, 5'11\" and 200 lbs, wearing the Souvenir Shirt in size Large", width: 844, height: 1500, photo: true, local: true },
      { url: "/img/souvenir-flat-front.webp", altText: "Souvenir Shirt, sun-faded front", width: 1125, height: 1500, local: true },
      { url: "/img/souvenir-flat-sleeve.webp", altText: "Souvenir Shirt, raw-edge sleeve detail", width: 1125, height: 1500, local: true },
      { url: "/img/souvenir-flat-hem.webp", altText: "Souvenir Shirt, print and raw hem detail", width: 1125, height: 1500, local: true },
    ],
  },
};

const withExtras = (product) => {
  const extra = EXTRAS[product?.handle];
  const base = productImages(product);
  if (!extra) return base;
  return [...extra.images, ...base.filter((im) => !extra.hide.some((h) => im.url.includes(h)))];
};

const Gallery = ({ images, title }) => {
  const [idx, setIdx] = useState(0);
  const [moved, setMoved] = useState(false);
  useEffect(() => { if (idx !== 0) setMoved(true); }, [idx]);
  const seen = useRef(new Set([0, 1]));
  const ref = useRef(null);
  const first = images[0]?.url;
  useEffect(() => { ref.current?.scrollTo({ left: 0, behavior: "smooth" }); setIdx(0); }, [first]);
  [idx - 1, idx, idx + 1].forEach((n) => seen.current.add(n));
  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const n = Math.round(el.scrollLeft / el.clientWidth);
    if (n !== idx) setIdx(n);
  };
  const go = (i) => {
    const el = ref.current;
    if (!el) return;
    const n = (i + images.length) % images.length;
    el.scrollTo({ left: n * el.clientWidth, behavior: "smooth" });
  };
  const Arrow = ({ dir }) => (
    <button
      type="button"
      data-testid={`gallery-${dir}-button`}
      aria-label={dir === "prev" ? "Previous image" : "Next image"}
      onClick={() => go(idx + (dir === "prev" ? -1 : 1))}
      className={`absolute top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center border border-[#111111]/15 bg-[#F4F2EE] transition-colors duration-300 hover:border-[#111111] hover:text-[#E2541B] ${dir === "prev" ? "left-3" : "right-3"}`}
    >
      {dir === "prev" ? <ChevronLeft size={20} strokeWidth={1.5} /> : <ChevronRight size={20} strokeWidth={1.5} />}
    </button>
  );
  return (
    <div className="relative w-full min-w-0 overflow-hidden">
      <div ref={ref} onScroll={onScroll} data-testid="product-gallery" className="no-scrollbar flex w-full snap-x snap-mandatory overflow-x-auto">
        {images.map((im, i) => {
          const wide = im.width && im.height && im.width / im.height > 1.6;
          return (
          <motion.div
            key={im.url}
            className="flex aspect-[4/5] w-full shrink-0 snap-center items-center justify-center bg-[#EEEBE5] md:aspect-auto md:h-[calc(100svh-150px)] md:min-h-[520px]"
            initial={i === 0 ? { opacity: 0, scale: 1.02 } : false}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            {seen.current.has(i) && <img
              src={im.local ? im.url : sized(im.url, 1000)}
              srcSet={im.local ? undefined : `${sized(im.url, 600)} 600w, ${sized(im.url, 1000)} 1000w`}
              sizes="(min-width: 768px) 58vw, 100vw"
              alt={im.altText || `${title} — image ${i + 1}`}
              decoding="async"
              className={im.photo ? "h-full w-full object-cover" : `object-contain mix-blend-multiply ${wide ? "h-auto w-full px-16 md:px-20" : im.local ? "h-full w-full p-[3%]" : "h-full w-full p-[12%]"}`}
            />}
          </motion.div>
          );
        })}
      </div>
      {images.length > 1 && (
        <>
          <SwipeHint hidden={moved} />
          <Arrow dir="prev" />
          <Arrow dir="next" />
          <div className="pointer-events-none absolute bottom-4 left-4 right-4 flex items-center gap-3" data-testid="gallery-indicator">
            <span className="label text-[10px]">{String(idx + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span>
            <div className="flex flex-1 gap-1.5">
              {images.map((_, i) => (
                <span key={i} className={`h-[2px] flex-1 transition-colors duration-500 ${i === idx ? "bg-[#111111]" : "bg-[#111111]/20"}`} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const optionValueAvailable = (variants, selected, name, value) =>
  variants.some((v) => v.availableForSale && v.selectedOptions.every((o) => (o.name === name ? o.value === value : !selected[o.name] || selected[o.name] === o.value)));

const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "XXL", "3XL", "XXXL", "4XL", "5XL"];
const sortValues = (name, values) =>
  /size/i.test(name)
    ? [...values].sort((a, b) => (SIZE_ORDER.indexOf(a.toUpperCase()) + 1 || 99) - (SIZE_ORDER.indexOf(b.toUpperCase()) + 1 || 99))
    : values;

const OptionPicker = ({ option, variants, selected, onPick, guide }) => (
  <fieldset className="mt-8">
    <div className="flex items-center justify-between">
      <legend className="label">{option.name}{selected[option.name] && <span className="ml-2 text-[#111111]/60">— {selected[option.name]}</span>}</legend>
      {/size/i.test(option.name) && guide}
    </div>
    <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-5">
      {sortValues(option.name, option.values).map((v) => {
        const active = selected[option.name] === v;
        const avail = optionValueAvailable(variants, selected, option.name, v);
        return (
          <button
            key={v}
            type="button"
            data-testid={`option-${option.name.toLowerCase()}-${v.toLowerCase().replace(/\s+/g, "-")}`}
            aria-pressed={active}
            disabled={!avail}
            onClick={() => onPick(option.name, v)}
            className={`label relative h-12 border text-[11px] transition-colors duration-300 disabled:cursor-not-allowed disabled:text-[#111111]/35 disabled:[background:linear-gradient(to_top_right,transparent_calc(50%-0.5px),rgba(17,17,17,0.2)_50%,transparent_calc(50%+0.5px))] ${
              active ? "border-[#111111] bg-[#111111] text-[#F4F2EE]" : "border-[#111111]/25 hover:border-[#111111]"
            }`}
          >
            {v}
          </button>
        );
      })}
    </div>
  </fieldset>
);

const splitDescription = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const fit = [];
  doc.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((h) => { if (/^\s*features\s*:?\s*$/i.test(h.textContent)) h.remove(); });
  doc.querySelectorAll("p,li").forEach((el) => {
    const m = el.textContent.match(/^\s*-?\s*fit\s*:\s*(.+)$/i);
    if (m) { fit.push(m[1].trim()); el.remove(); }
  });
  return { details: doc.body.innerHTML, fit };
};

const showGalleryOnPhone = () => {
  const el = document.querySelector('[data-testid="product-gallery"]');
  if (!el || window.innerWidth >= 768 || el.getBoundingClientRect().top > 0) return;
  if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -72 });
  else window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 72, behavior: "smooth" });
};

const InfoPanel = ({ product, onImage }) => {
  const { add, busy } = useCart();
  const variants = useMemo(() => product?.variants?.nodes ?? [], [product]);
  const options = (product?.options ?? []).filter((o) => !(o.values.length === 1 && o.values[0] === "Default Title"));
  const [selected, setSelected] = useState({});
  const [params] = useSearchParams();
  const preColor = params.get("color");

  useEffect(() => {
    const init = {};
    (product?.options ?? []).forEach((o) => {
      if (o.values.length === 1) init[o.name] = o.values[0];
      else if (preColor && /colou?r/i.test(o.name) && o.values.includes(preColor)) init[o.name] = preColor;
    });
    setSelected(init);
  }, [product, preColor]);

  const variant = variants.find((v) => v.selectedOptions.every((o) => selected[o.name] === o.value));
  const preview = variant || variants.find((v) => Object.entries(selected).every(([n, val]) => v.selectedOptions.some((o) => o.name === n && o.value === val)));
  const previewUrl = Object.keys(selected).length ? preview?.image?.url : null;
  useEffect(() => { if (previewUrl) onImage(previewUrl); }, [previewUrl, onImage]);
  const price = variant?.price || product?.priceRange?.minVariantPrice;
  const compare = variant?.compareAtPrice;
  const missing = options.find((o) => !selected[o.name]);
  const soldOut = !product.availableForSale || (variant && !variant.availableForSale);

  const desc = useMemo(() => splitDescription(product?.descriptionHtml), [product]);
  const modelNote = EXTRAS[product?.handle]?.model;
  const chartImage = productImages(product).find((im) => /size[-_]?(guide|chart)/i.test(im.url))?.url;
  const guide = <SizeGuide chart={EXTRAS[product?.handle]?.sizeChart} image={chartImage} note={modelNote} />;
  const fitList = desc.fit.length ? <ul className="space-y-1">{desc.fit.map((f) => <li key={f}>- {f}</li>)}</ul> : null;
  const fitBase = product?.fit?.value || fitList || "Fit notes for this piece are coming soon. If you're unsure between sizes, reach out before ordering.";
  const fit = <>{fitBase}{modelNote && <p className="mt-3">{modelNote}</p>}</>;
  const shipping = product?.shipping?.value || "Shipping options and delivery times are shown at checkout. Questions about returns? Get in touch and we'll help.";

  return (
    <div className="md:sticky md:top-[96px]">
      {product.productType && <p className="label text-[#111111]/60">{product.productType}</p>}
      <h1 data-testid="product-title" className="mt-3 font-['Geist',sans-serif] text-4xl font-light uppercase leading-[0.95] tracking-[-0.04em] sm:text-5xl lg:text-6xl">{product.title}</h1>
      <p data-testid="product-price" className="label mt-5 text-sm">
        {compare && Number(compare.amount) > Number(price?.amount) && <s className="mr-2 text-[#111111]/50">{money(compare)}</s>}
        {money(price)}
      </p>
      {options.map((o) => (
        <OptionPicker key={o.name} guide={guide} option={o} variants={variants} selected={selected} onPick={(n, v) => { setSelected((s) => ({ ...s, [n]: v })); if (/colou?r/i.test(n)) showGalleryOnPhone(); }} />
      ))}
      {modelNote && <p data-testid="model-note" className="label mt-4 text-[10px] text-[#111111]/65">{modelNote}</p>}
      <button
        data-testid="add-to-cart-button"
        disabled={busy || soldOut || !!missing || !variant}
        onClick={() => add(variant.id, 1)}
        className="label group relative mt-8 flex h-14 w-full items-center justify-center overflow-hidden bg-[#111111] text-[#F4F2EE] disabled:bg-[#111111]/40"
      >
        <span className="absolute inset-0 origin-left scale-x-0 bg-[#E2541B] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-enabled:group-hover:scale-x-100" />
        <span className="relative">{soldOut ? "Sold out" : missing ? `Select ${missing.name.toLowerCase()}` : busy ? "Adding…" : "Add to cart"}</span>
      </button>
      <Accordion type="single" collapsible className="mt-10 border-t" data-testid="product-accordions">
        {[
          { k: "details", t: "Details", c: <div className="prose-sm space-y-3 [&_li]:ml-4 [&_li]:list-disc" dangerouslySetInnerHTML={{ __html: desc.details || "<p>—</p>" }} /> },
          { k: "fit", t: "Fit", c: <div>{fit}</div> },
          { k: "shipping", t: "Shipping & Returns", c: <p>{shipping}</p> },
        ].map((s) => (
          <AccordionItem key={s.k} value={s.k} className="border-b">
            <AccordionTrigger data-testid={`accordion-${s.k}`} className="label py-5 hover:text-[#E2541B] hover:no-underline">{s.t}</AccordionTrigger>
            <AccordionContent className="pb-6 text-sm leading-relaxed text-[#111111]/80">{s.c}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default function ProductPage() {
  const { handle } = useParams();
  const { data, loading, error } = useApi(`/shop/products/${handle}`);
  const product = data?.product;
  const recs = Array.isArray(data?.recommendations) ? data.recommendations : [];
  const [lead, setLead] = useState(null);
  const all = withExtras(product);
  const leadImg = all.find((im) => im.url === lead);
  const images = leadImg ? [leadImg, ...all.filter((im) => im !== leadImg)] : all;
  useEffect(() => { setLead(null); }, [handle]);

  if (loading) return <div className="grid min-h-[70vh] place-items-center"><p className="label animate-pulse">Loading</p></div>;
  if (error || !product)
    return (
      <div className="grid min-h-[70vh] place-items-center text-center" data-testid="product-not-found">
        <div><p className="font-display text-5xl font-bold tracking-[-0.05em]">Not found.</p><Link to="/shop" className="label draw-link mt-6 inline-block">Back to shop</Link></div>
      </div>
    );

  return (
    <div data-testid="product-page">
      <nav className="label mx-auto flex max-w-[1600px] gap-2 px-4 py-5 text-[10px] text-[#111111]/60 md:px-8">
        <Link to="/shop" className="draw-link">Shop</Link><span>/</span><span className="text-[#111111]">{product.title}</span>
      </nav>
      <section className="mx-auto grid max-w-[1600px] grid-cols-1 gap-8 md:grid-cols-12 md:gap-10 md:px-8">
        <div className="min-w-0 md:col-span-7">{images.length > 0 && <Gallery images={images} title={product.title} />}</div>
        <div className="min-w-0 px-4 pb-10 md:col-span-5 md:px-0 lg:pl-8">
          <InfoPanel product={product} onImage={setLead} />
        </div>
      </section>
      {recs.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-4 py-24 md:px-8 md:py-32">
          <Reveal className="mb-10 flex items-end justify-between border-b pb-5">
            <h2 className="text-4xl font-bold uppercase leading-[0.9] tracking-[-0.055em] sm:text-5xl lg:text-6xl">You may<br />also like</h2>
          </Reveal>
          <Reveal><ProductGrid products={recs.slice(0, 4)} testid="recommendations-grid" /></Reveal>
        </section>
      )}
    </div>
  );
}
